/**
 * Notification Template models - aligned with backend Alaric.Services.Messaging
 *
 * Templates are MERCHANT-SPECIFIC and contain the actual notification content.
 * Each template is associated with an event definition and a channel,
 * and can have multiple localizations (one per language).
 */

import { NotificationChannel, TemplateStatus } from "./NotificationEnums";

// ============================================================================
// Localization DTOs
// ============================================================================

/**
 * Response DTO for template localization.
 * Maps to: Alaric.Services.Messaging.Dto.LocalizationDto
 */
export interface LocalizationDto {
    /** Unique localization identifier (GUID) */
    localizationId: string;

    /** Language identifier (GUID) */
    languageId: string;

    /** ISO language code (e.g., "en", "bg") */
    languageIso: string;

    /** Language display name (e.g., "English", "Bulgarian") */
    languageName: string;

    /** Email subject line (required for Email channel) */
    subject?: string;

    /** Notification title (used for Push/Pull channels) */
    title?: string;

    /** Notification body content with {{placeholders}} */
    body: string;
}

/**
 * Request DTO for creating a new localization.
 * Maps to: Alaric.Services.Messaging.Dto.CreateLocalizationRequest
 */
export interface CreateLocalizationRequest {
    /** Language identifier (GUID) */
    languageId: string;

    /** Email subject line */
    subject?: string;

    /** Notification title */
    title?: string;

    /** Notification body content */
    body: string;
}

/**
 * Request DTO for updating a localization.
 * Maps to: Alaric.Services.Messaging.Dto.UpdateLocalizationRequest
 */
export interface UpdateLocalizationRequest {
    /** Localization ID - null means create new */
    localizationId?: string | null;

    /** Language identifier (GUID) */
    languageId: string;

    /** Email subject line */
    subject?: string;

    /** Notification title */
    title?: string;

    /** Notification body content */
    body: string;
}

// ============================================================================
// Template DTOs
// ============================================================================

/**
 * Full template DTO with all localizations.
 * Maps to: Alaric.Services.Messaging.Dto.TemplateDto
 */
export interface TemplateDto {
    /** Unique template identifier (GUID) */
    templateId: string;

    /** Merchant ID this template belongs to */
    merchantId: number;

    /** Event code this template is for */
    eventCode: string;

    /** Notification channel */
    channel: NotificationChannel;

    /** Template status */
    status: TemplateStatus;

    /**
     * Deep link URL template with {{placeholders}}.
     * Used for Push notifications to enable click-through navigation.
     * Example: "myapp://orders/{{orderId}}/details"
     */
    deepLinkTemplate?: string;

    /**
     * External URL to open in the system browser when the notification is tapped.
     * Mutually exclusive with deepLinkTemplate.
     */
    externalUrl?: string;

    /** When the template was created */
    createdAt: string;

    /** Who created the template */
    createdBy?: string;

    /** When the template was last updated */
    updatedAt?: string;

    /** Who last updated the template */
    updatedBy?: string;

    /** Template localizations */
    localizations: LocalizationDto[];
}

/**
 * Template list item DTO (summary without full localizations).
 * Maps to: Alaric.Services.Messaging.Dto.TemplateListItemDto
 */
export interface TemplateListItemDto {
    /** Unique template identifier (GUID) */
    templateId: string;

    /** Event code this template is for */
    eventCode: string;

    /** Notification channel */
    channel: NotificationChannel;

    /** Template status */
    status: TemplateStatus;

    /**
     * Deep link URL template (if configured).
     */
    deepLinkTemplate?: string;

    /** External URL to open in the browser on tap */
    externalUrl?: string;

    /** Number of localizations */
    localizationCount: number;

    /** When the template was created */
    createdAt: string;
}

/**
 * Request DTO for creating a new template.
 * Maps to: Alaric.Services.Messaging.Dto.CreateTemplateRequest
 */
export interface CreateTemplateRequest {
    /** Merchant ID this template belongs to */
    merchantId: number;

    /** Event code this template is for */
    eventCode: string;

    /** Notification channel */
    channel: NotificationChannel;

    /**
     * Deep link URL template with {{placeholders}}.
     * Max 500 characters.
     */
    deepLinkTemplate?: string;

    /** External URL to open in the browser. Mutually exclusive with deepLinkTemplate. */
    externalUrl?: string;

    /** Who is creating the template */
    createdBy?: string;

    /** Initial localizations */
    localizations: CreateLocalizationRequest[];
}

/**
 * Request DTO for updating a template.
 * Maps to: Alaric.Services.Messaging.Dto.UpdateTemplateRequest
 */
export interface UpdateTemplateRequest {
    /** New status (optional) */
    status?: TemplateStatus;

    /**
     * Deep link URL template with {{placeholders}}.
     * Max 500 characters. Set to empty string to clear.
     */
    deepLinkTemplate?: string;

    /** External URL to open in the browser. Mutually exclusive with deepLinkTemplate. */
    externalUrl?: string;

    /** Who is updating the template */
    updatedBy?: string;

    /** Updated localizations (optional) */
    localizations?: UpdateLocalizationRequest[];
}

// ============================================================================
// Search/Filter types
// ============================================================================

/**
 * Search parameters for templates
 */
export interface TemplateSearchParams {
    /** Merchant ID to filter by (required) */
    merchantId: number;

    /** Filter by event code */
    eventCode?: string;

    /** Filter by channel */
    channel?: NotificationChannel;

    /** Filter by status */
    status?: TemplateStatus;

    /** Page number (1-based) */
    pageNumber?: number;

    /** Page size */
    pageSize?: number;
}

// ============================================================================
// Helper functions
// ============================================================================

/**
 * Create an empty template form object
 */
export const createEmptyTemplate = (merchantId: number): CreateTemplateRequest => ({
    merchantId,
    eventCode: "",
    channel: NotificationChannel.Email,
    localizations: [],
});

/**
 * Create an empty localization form object
 */
export const createEmptyLocalization = (): CreateLocalizationRequest => ({
    languageId: "",
    subject: "",
    title: "",
    body: "",
});

/**
 * Check if template requires subject (Email channel)
 */
export const requiresSubject = (channel: NotificationChannel): boolean => {
    return channel === NotificationChannel.Email;
};

/**
 * Check if template requires title (Push/Pull channels)
 */
export const requiresTitle = (channel: NotificationChannel): boolean => {
    return channel === NotificationChannel.Push || channel === NotificationChannel.Pull;
};

/**
 * Extract placeholders from template content
 */
export const extractPlaceholders = (content: string): string[] => {
    const matches = content.match(/\{\{([^}]+)\}\}/g) || [];
    return [...new Set(matches.map((m) => m.replace(/[{}]/g, "").trim()))];
};

/**
 * Check if all required placeholders are present in allowed variables
 */
export const validatePlaceholders = (
    content: string,
    allowedVariables: string[],
): { valid: boolean; unknownPlaceholders: string[] } => {
    const usedPlaceholders = extractPlaceholders(content);
    const unknownPlaceholders = usedPlaceholders.filter((p) => !allowedVariables.includes(p));

    return {
        valid: unknownPlaceholders.length === 0,
        unknownPlaceholders,
    };
};
